import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiCopy,
  FiMessageSquare,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiUser,
} from "react-icons/fi";
import { getSupabase, supabasePublic, supabaseSchema } from "../supabaseClient";
import { tableName } from "../env";
import { formatPhone, formatVisitDate, timeAgo } from "../../utils/failedNumbers";
import { filterNumberRows, notTextedNumbers, patientDialableNumbers } from "../../utils/numberLists";
import { onNumberStatusChange } from "../../utils/numberStatus";
import { EMPTY_RANGE, filterByDate } from "../../utils/dateRange";
import DateRangeFilter from "./DateRangeFilter";
import {
  Fact,
  RecentTexts,
  attachPatients,
  formatDateTime,
  isMissingTable,
  loadRecentTexts,
  providerName,
} from "./FailedNumbersPanel";

// The "Good numbers" and "Not texted" tabs of the SMS Status modal, next to
// "Failed numbers". Same data as PhoneStatusBadge: a number is good when
// public.sms_number_health says texts reach it, and not texted when that table
// has no row for it at all.

const PAGE = 1000; // PostgREST's default max rows per request
const CHUNK = 100; // keeps `in.(...)` URLs short
const SHOW_STEP = 25;

// Numbers whose texts are getting through (verdict good), plus ones texted so
// recently that Twilio has not reported back yet (verdict unknown).
export const loadGoodNumbers = async (schemas) => {
  const client = supabasePublic();
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await client
      .from("sms_number_health")
      .select("*")
      .in("schema_name", schemas)
      .in("verdict", ["good", "unknown"])
      .order("last_seen_at", { ascending: false, nullsFirst: false })
      .order("schema_name", { ascending: true })
      .order("phone_number", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return attachPatients(rows);
};

// Patient numbers on file in the providers' "notes-c" that no clinic has ever
// texted. A provider whose patients cannot be read is skipped, not fatal.
export const loadNotTextedNumbers = async (schemas) => {
  const supabase = await getSupabase();
  const patientsBySchema = {};
  const failed = [];
  await Promise.all(
    schemas.map(async (schema) => {
      try {
        // Both phone columns; a schema that predates home_phone (added
        // 2026-10-17) errors on the column, so fall back to the cell alone.
        const page = async (from, withHome) => {
          let q = supabase
            .schema(schema)
            .from(tableName)
            .select(withHome ? "id, patient_name, tel_num, home_phone, date_" : "id, patient_name, tel_num, date_");
          if (!withHome) q = q.not("tel_num", "is", null);
          return q.order("id", { ascending: true }).range(from, from + PAGE - 1);
        };
        let withHome = true;
        const rows = [];
        for (let from = 0; ; from += PAGE) {
          let { data, error } = await page(from, withHome);
          if (error && withHome && from === 0) {
            withHome = false;
            ({ data, error } = await page(from, withHome));
          }
          if (error) throw error;
          rows.push(...(data || []));
          if (!data || data.length < PAGE) break;
        }
        patientsBySchema[schema] = rows.filter((r) => r.tel_num || r.home_phone);
      } catch (e) {
        console.warn(`[Not texted] could not load patients for "${schema}":`, e?.message || e);
        failed.push(schema);
      }
    }),
  );
  if (failed.length === schemas.length) {
    throw new Error(`Could not load patients for: ${failed.join(", ")}`);
  }

  const numbers = [
    ...new Set(Object.values(patientsBySchema).flat().flatMap(patientDialableNumbers)),
  ];
  const texted = new Set();
  const client = supabasePublic();
  for (let i = 0; i < numbers.length; i += CHUNK) {
    const { data, error } = await client
      .from("sms_number_health")
      .select("phone_number")
      .in("phone_number", numbers.slice(i, i + CHUNK));
    if (error) throw error;
    (data || []).forEach((r) => texted.add(r.phone_number));
  }
  return { rows: notTextedNumbers(patientsBySchema, texted), failedProviders: failed.sort() };
};

const PatientChips = ({ patients, border }) => (
  <Wrap spacing={2}>
    {patients.map((p) => (
      <WrapItem key={p.id}>
        <HStack spacing={2} borderWidth="1px" borderColor={border} borderRadius="full" pl={2} pr={3} py={1}>
          <Icon as={FiUser} color="gray.400" boxSize={3.5} />
          <Text fontSize="sm" fontWeight="medium">
            {p.name || "Unnamed patient"}
          </Text>
          {p.lastVisit && (
            <Text fontSize="xs" color="gray.500">
              last visit {formatVisitDate(p.lastVisit)}
            </Text>
          )}
        </HStack>
      </WrapItem>
    ))}
  </Wrap>
);

const CopyButton = ({ phone }) => {
  const toast = useToast();
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(phone);
      toast({ title: `Copied ${formatPhone(phone)}`, status: "success", duration: 1500 });
    } catch {
      toast({ title: "Could not copy", status: "error", duration: 2000 });
    }
  };
  return (
    <Button size="xs" variant="outline" leftIcon={<FiCopy />} onClick={copy}>
      Copy number
    </Button>
  );
};

// The newest text to this number, shown on the card without opening it.
const LastText = ({ phone, schema, load }) => {
  const [text, setText] = useState(undefined);
  const muted = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    let cancelled = false;
    load(phone, schema)
      .then((data) => !cancelled && setText((data || [])[0] || null))
      .catch(() => !cancelled && setText(null));
    return () => {
      cancelled = true;
    };
  }, [phone, schema, load]);

  if (text === undefined) return <Skeleton h="14px" mt={1} w="70%" borderRadius="sm" />;
  if (!text) {
    return (
      <Text fontSize="xs" color={muted} mt={0.5}>
        Last SMS could not be loaded
      </Text>
    );
  }
  return (
    <HStack spacing={1.5} mt={0.5} fontSize="xs" color={muted} minW={0} data-testid="last-sms">
      <Icon as={FiMessageSquare} boxSize={3} flexShrink={0} />
      <Text as="span" flexShrink={0} fontWeight="semibold">
        {timeAgo(text.created_at)}
      </Text>
      <Text as="span" noOfLines={1}>
        {text.message || "(no text)"}
      </Text>
    </HStack>
  );
};

const CardShell = ({ isOpen, onToggle, accent, icon, iconScheme, children, details, testId }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const iconBg = useColorModeValue(`${iconScheme}.50`, "whiteAlpha.100");
  const iconColor = useColorModeValue(`${iconScheme}.500`, `${iconScheme}.300`);
  return (
    <Box
      data-testid={testId}
      bg={cardBg}
      borderWidth="1px"
      borderColor={isOpen ? accent : border}
      borderRadius="xl"
      overflow="hidden"
      transition="border-color 0.15s"
    >
      <Flex
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        align="center"
        gap={3}
        px={3}
        py={2.5}
        onClick={onToggle}
        _hover={{ bg: hoverBg }}
        aria-expanded={isOpen}
      >
        <Center boxSize={9} borderRadius="full" bg={iconBg} color={iconColor} flexShrink={0}>
          <Icon as={icon} boxSize={4} />
        </Center>
        {children}
        <Icon
          as={FiChevronDown}
          color="gray.400"
          transition="transform 0.2s"
          transform={isOpen ? "rotate(180deg)" : "none"}
        />
      </Flex>
      <Collapse in={isOpen} animateOpacity unmountOnExit>
        <Box px={3} pb={3} pt={1} borderTopWidth="1px" borderColor={border}>
          {details}
        </Box>
      </Collapse>
    </Box>
  );
};

const NameLine = ({ row, children }) => {
  const names = row.patient_names || [];
  const phone = formatPhone(row.phone_number);
  return (
    <HStack spacing={2} wrap="wrap">
      {names.length ? (
        <Text fontWeight="semibold" fontSize="sm" noOfLines={1} data-testid="patient-name">
          {names[0]}
          {names.length > 1 && (
            <Text as="span" fontWeight="normal" color="gray.500">
              {" "}
              +{names.length - 1} more
            </Text>
          )}
        </Text>
      ) : (
        <Text fontWeight="semibold" fontFamily="mono" fontSize="sm">
          {phone}
        </Text>
      )}
      {children}
    </HStack>
  );
};

const ProviderBadge = ({ label }) =>
  label ? (
    <Badge variant="outline" colorScheme="gray" borderRadius="full" px={2} data-testid="provider">
      {label}
    </Badge>
  ) : null;

const GoodNumberCard = ({ row, isOpen, onToggle, loadTexts, providerLabel }) => {
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const pending = row.verdict !== "good";
  const names = row.patient_names || [];
  return (
    <CardShell
      testId="good-number"
      isOpen={isOpen}
      onToggle={onToggle}
      accent={pending ? "gray.300" : "green.300"}
      icon={pending ? FiClock : FiCheckCircle}
      iconScheme={pending ? "gray" : "green"}
      details={
        <>
          <Box mt={2}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
              {names.length > 1 ? "Patients with this number" : "Patient"}
            </Text>
            {names.length ? (
              <PatientChips patients={row.patients} border={border} />
            ) : (
              <Text fontSize="sm" color="gray.500">
                No patient on file has this number now.
              </Text>
            )}
          </Box>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={3}>
            <Fact label="Texts">
              {row.delivered_count} delivered · {row.failed_count} failed · {row.total_messages} total
            </Fact>
            <Fact label="Last delivered">{formatDateTime(row.last_success_at)}</Fact>
            <Fact label="Last texted">{formatDateTime(row.last_seen_at)}</Fact>
            <Fact label="First texted">{formatDateTime(row.first_seen_at)}</Fact>
            <Fact label="Do Not Text">{row.on_do_not_text ? "Yes" : "No"}</Fact>
          </SimpleGrid>
          <Box mt={3}>
            <HStack spacing={1.5} mb={1.5} color="gray.500">
              <Icon as={FiMessageSquare} boxSize={3} />
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                Recent texts
              </Text>
            </HStack>
            <RecentTexts phone={row.phone_number} schema={row.schema_name} load={loadTexts} />
          </Box>
          <Flex justify="flex-end" mt={3}>
            <CopyButton phone={row.phone_number} />
          </Flex>
        </>
      }
    >
      <Box flex="1" minW={0}>
        <NameLine row={row}>
          <Badge colorScheme={pending ? "gray" : "green"} variant="subtle" borderRadius="full" px={2}>
            {pending ? "No result yet" : "Good number"}
          </Badge>
          <ProviderBadge label={providerLabel} />
        </NameLine>
        {names.length > 0 && (
          <Text fontSize="xs" color="gray.500" fontFamily="mono">
            {formatPhone(row.phone_number)}
          </Text>
        )}
        <LastText phone={row.phone_number} schema={row.schema_name} load={loadTexts} />
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="sm" fontWeight="semibold">
          {row.delivered_count} delivered
        </Text>
        <Text fontSize="xs" color="gray.500">
          {timeAgo(row.last_seen_at)}
        </Text>
      </Box>
    </CardShell>
  );
};

const NotTextedCard = ({ row, isOpen, onToggle, providerLabel }) => {
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const names = row.patient_names || [];
  return (
    <CardShell
      testId="not-texted-number"
      isOpen={isOpen}
      onToggle={onToggle}
      accent="gray.300"
      icon={FiSend}
      iconScheme="gray"
      details={
        <>
          <Box mt={2}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
              {row.patients.length > 1 ? "Patients with this number" : "Patient"}
            </Text>
            <PatientChips patients={row.patients} border={border} />
          </Box>
          <Flex justify="flex-end" mt={3}>
            <CopyButton phone={row.phone_number} />
          </Flex>
        </>
      }
    >
      <Box flex="1" minW={0}>
        <NameLine row={row}>
          <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
            Not texted yet
          </Badge>
          <ProviderBadge label={providerLabel} />
        </NameLine>
        {names.length > 0 && (
          <Text fontSize="xs" color="gray.500" fontFamily="mono">
            {formatPhone(row.phone_number)}
          </Text>
        )}
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="xs" color="gray.500">
          last visit
        </Text>
        <Text fontSize="sm" fontWeight="semibold">
          {row.last_visit ? formatVisitDate(row.last_visit) : "—"}
        </Text>
      </Box>
    </CardShell>
  );
};

// Loading, errors, header, search, provider filter and paging for both tabs.
const NumberList = ({
  isActive,
  schemas,
  homeSchema,
  load: loadRows,
  title,
  subtitle,
  noun,
  emptyIcon,
  emptyTitle,
  emptyText,
  renderCard,
  dateField,
  dateLabel,
}) => {
  const providers = useMemo(
    () => (schemas && schemas.length ? schemas : [supabaseSchema || "public"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(schemas || []).join(",")],
  );
  const home = homeSchema || supabaseSchema;
  const [state, setState] = useState({ status: "idle", rows: [], failedProviders: [], error: null, at: null });
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("");
  const [dates, setDates] = useState(EMPTY_RANGE);
  const [openKey, setOpenKey] = useState(null);
  const [shown, setShown] = useState(SHOW_STEP);
  const muted = useColorModeValue("gray.600", "gray.400");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: s.rows.length ? "refreshing" : "loading", error: null }));
    try {
      const { rows, failedProviders = [] } = await loadRows(providers);
      setState({ status: "ready", rows, failedProviders, error: null, at: new Date().toISOString() });
    } catch (error) {
      setState((s) => ({ ...s, status: isMissingTable(error) ? "missing" : "error", error }));
    }
  }, [providers, loadRows]);

  // Load the first time the tab is opened, not when the modal mounts.
  useEffect(() => {
    if (isActive && state.status === "idle") load();
  }, [isActive, state.status, load]);

  // After an SMS send, an open tab reloads so a number moves between lists.
  useEffect(() => {
    if (!isActive) return undefined;
    return onNumberStatusChange(() => load());
  }, [isActive, load]);

  useEffect(() => setShown(SHOW_STEP), [search, provider, dates]);

  const allRows = state.rows;
  const rowProviders = useMemo(() => [...new Set(allRows.map((r) => r.schema_name))].sort(), [allRows]);
  const multiProvider = rowProviders.length > 1;
  const visible = useMemo(
    () => filterNumberRows(dateField ? filterByDate(allRows, dateField, dates) : allRows, { search, provider }),
    [allRows, search, provider, dateField, dates],
  );

  if (state.status === "idle" || state.status === "loading") {
    return (
      <Center py={10} flexDirection="column" gap={3}>
        <Spinner size="lg" color="teal.500" />
        <Text color={muted}>Loading {noun}…</Text>
      </Center>
    );
  }

  if (state.status === "missing") {
    return (
      <Alert status="info" borderRadius="lg" alignItems="start">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Number tracking isn't set up on this database yet. Run{" "}
          <Text as="span" fontFamily="mono">
            sql/migrations/2026-09-22-sms-number-health.sql
          </Text>{" "}
          in Supabase, then reopen this tab.
        </AlertDescription>
      </Alert>
    );
  }

  if (state.status === "error") {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertDescription fontSize="sm" flex="1">
          Could not load {noun}: {state.error?.message || "unknown error"}
        </AlertDescription>
        <Button size="sm" onClick={load}>
          Try again
        </Button>
      </Alert>
    );
  }

  const skipped = state.failedProviders.length > 0 && (
    <Alert status="warning" borderRadius="md" py={2}>
      <AlertIcon />
      <AlertDescription fontSize="sm">
        Could not read patients for {state.failedProviders.join(", ")}; their numbers are not listed.
      </AlertDescription>
    </Alert>
  );

  if (allRows.length === 0) {
    return (
      <VStack spacing={3} align="stretch">
        {skipped}
        <Center py={10} flexDirection="column" gap={2} textAlign="center" data-testid="number-list-empty">
          <Icon as={emptyIcon} boxSize={10} color="gray.300" />
          <Text fontWeight="semibold">{emptyTitle}</Text>
          <Text fontSize="sm" color={muted}>
            {emptyText}
          </Text>
          <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={load} mt={2}>
            Refresh
          </Button>
        </Center>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="center" gap={2} wrap="wrap">
        <Box>
          <Text fontWeight="bold">
            {title}{" "}
            <Badge colorScheme="gray" borderRadius="full" ml={1}>
              {allRows.length}
            </Badge>
          </Text>
          <Text fontSize="xs" color={muted}>
            {subtitle} · loaded {timeAgo(state.at)}
          </Text>
        </Box>
        <Tooltip label="Reload" hasArrow>
          <IconButton
            size="sm"
            variant="outline"
            aria-label={`Reload ${noun}`}
            icon={<FiRefreshCw />}
            isLoading={state.status === "refreshing"}
            onClick={load}
          />
        </Tooltip>
      </Flex>

      {skipped}

      <Stack direction={{ base: "column", md: "row" }} spacing={2}>
        <InputGroup size="sm" flex="2">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            borderRadius="md"
            placeholder="Search patient or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        {multiProvider && (
          <Select
            size="sm"
            borderRadius="md"
            flex="1"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            aria-label="Filter by provider"
          >
            <option value="">All providers ({allRows.length})</option>
            {rowProviders.map((s) => (
              <option key={s} value={s}>
                {providerName(s, home)} ({allRows.filter((r) => r.schema_name === s).length})
              </option>
            ))}
          </Select>
        )}
        {dateField && <DateRangeFilter value={dates} onChange={setDates} fieldLabel={dateLabel} />}
      </Stack>

      {visible.length === 0 ? (
        <Center py={8} flexDirection="column" gap={2} textAlign="center">
          <Icon as={FiSearch} boxSize={7} color="gray.300" />
          <Text fontSize="sm" color={muted}>
            No numbers match this search.
          </Text>
          <Button
            size="xs"
            variant="link"
            colorScheme="teal"
            onClick={() => {
              setSearch("");
              setProvider("");
              setDates(EMPTY_RANGE);
            }}
          >
            Clear filters
          </Button>
        </Center>
      ) : (
        <Stack spacing={2}>
          <Text fontSize="xs" color={muted}>
            Showing {Math.min(shown, visible.length)} of {visible.length}
          </Text>
          {visible.slice(0, shown).map((row) => {
            const key = `${row.schema_name}:${row.phone_number}`;
            return (
              <React.Fragment key={key}>
                {renderCard(row, {
                  isOpen: openKey === key,
                  onToggle: () => setOpenKey(openKey === key ? null : key),
                  providerLabel: multiProvider ? providerName(row.schema_name, home) : null,
                })}
              </React.Fragment>
            );
          })}
          {shown < visible.length && (
            <Button size="sm" variant="ghost" onClick={() => setShown((n) => n + SHOW_STEP)}>
              Show {Math.min(SHOW_STEP, visible.length - shown)} more
            </Button>
          )}
        </Stack>
      )}
    </VStack>
  );
};

export const GoodNumbersPanel = ({
  isActive = true,
  schemas,
  homeSchema,
  loadData = loadGoodNumbers,
  loadTexts = loadRecentTexts,
}) => {
  const load = useCallback(async (providers) => ({ rows: await loadData(providers) }), [loadData]);
  // A card's last-SMS line and its "Recent texts" share one request.
  const cachedTexts = useMemo(() => {
    const cache = new Map();
    return (phone, schema) => {
      const key = `${schema}:${phone}`;
      if (!cache.has(key)) {
        cache.set(
          key,
          loadTexts(phone, schema).catch((e) => {
            cache.delete(key);
            throw e;
          }),
        );
      }
      return cache.get(key);
    };
  }, [loadTexts]);

  return (
    <NumberList
      isActive={isActive}
      schemas={schemas}
      homeSchema={homeSchema}
      load={load}
      title="Numbers your texts reach"
      subtitle="Most recently texted first"
      noun="good numbers"
      emptyIcon={FiMessageSquare}
      emptyTitle="No delivered texts yet"
      emptyText="Numbers show up here once a text to them is delivered."
      renderCard={(row, props) => <GoodNumberCard row={row} loadTexts={cachedTexts} {...props} />}
      dateField="last_seen_at"
      dateLabel="last text"
    />
  );
};

export const NotTextedPanel = ({ isActive = true, schemas, homeSchema, loadData = loadNotTextedNumbers }) => (
  <NumberList
    isActive={isActive}
    schemas={schemas}
    homeSchema={homeSchema}
    load={loadData}
    title="Patients never texted"
    subtitle="Numbers on file with no text sent, most recent visit first"
    noun="numbers not texted"
    emptyIcon={FiCheckCircle}
    emptyTitle="Every patient number has been texted"
    emptyText="No patient on file has a number that was never texted."
    renderCard={(row, props) => <NotTextedCard row={row} {...props} />}
  />
);
